import React, { useEffect } from 'react';

import { Routes, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';

import {
  getIsUserApproved,
  getIsAiOnboardingCallActive,
  getIsAiOnboardingProfileSubmission,
  getPromoUsed,
  resetApproval,
  resetPromoUsed,
  resetSignupProfilePortingStatus,
  resetStatus,
  setSignedUp,
  successOrSelector,
  userActions,
  ServiceTypes,
  successSelector,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppSelector } from '../hooks/useAppSelector';
import CustomDialog from '../components/common/CustomModal/CustomDialog';
import { setDialog } from '../reducers/dialogReducer';
import { useAppDispatch } from '../hooks/useAppDispatch';
import ApprovalModal from '../components/Modals/ApprovalModal';
import PromoApprovalModal from '../components/Modals/PromoApprovalModal/PromoApprovalModal';

import theme from '../theme';
import LoginRouter from './LoginRouter';
import AdminRouter from './AdminRouter';
import PharmaRouter from './PharmaRouter';
import PharmacyAdminRouter from './PharmacyAdminRouter';
import useAdmin from '../hooks/useAdmin';
import { Constants } from '../helpers/Constants';
import {
  getPendingAiOnboardingAudioDownload,
} from '../helpers/adminAiOnboardingAudioDownload';

const { createProfessionalProfile, createProfile } = userActions;
const { full } = ServiceTypes;

const AppRouter = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const isApproved = useAppSelector(getIsUserApproved);
  const isAiOnboardingProfileSubmission = useAppSelector(
    getIsAiOnboardingProfileSubmission,
  );
  const isPromoUsed = useAppSelector(getPromoUsed);
  const isAiOnboardingCallActive = useAppSelector(getIsAiOnboardingCallActive);
  const isSignupSuccess = useAppSelector((state) =>
    successOrSelector([createProfile, createProfessionalProfile], state));
  // An AI onboarding profile is created before the agent's closing message,
  // so its approval modal waits until the call has ended.
  const isSignupComplete = isSignupSuccess && !isAiOnboardingCallActive;
  const isProfessionalSignupSuccess = useAppSelector((state) =>
    successSelector([createProfessionalProfile], state));
  const showCalendlyBooking = ServiceHandler.getService() === full
    && isProfessionalSignupSuccess
    && !isAiOnboardingProfileSubmission;

  const showApprovalPending = () => {
    dispatch(
      setDialog({
        showCloseButton: false,
        Component: isPromoUsed ? (
          <PromoApprovalModal showCalendlyBooking={showCalendlyBooking} />
        ) : (
          <ApprovalModal
            showCalendlyBooking={showCalendlyBooking}
            showCloseButton
          />
        ),
        heading: {
          title: '',
        },
      }),
    );
  };

  useEffect(() => {
    if (isSignupComplete || !isApproved) {
      showApprovalPending();
      dispatch(setSignedUp(false));
      dispatch(resetApproval());
      dispatch(resetPromoUsed());
      dispatch(resetSignupProfilePortingStatus());
      dispatch(resetStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignupComplete, isApproved]);

  useEffect(() => {
    const pendingDownload = getPendingAiOnboardingAudioDownload();

    if (isAdmin && pendingDownload) {
      const { blobUrl, fileName } = pendingDownload;
      const searchParams = new URLSearchParams({ blobUrl });

      if (fileName) {
        searchParams.set('fileName', fileName);
      }

      navigate(
        `${Constants.paths.admin.aiOnboardingAudioDownload}?${searchParams.toString()}`,
        { replace: true },
      );
    }
  }, [isAdmin, navigate]);

  return (
    <ThemeProvider theme={theme}>
      <CustomDialog />
      <Routes>
        {/* PRIVATE ROUTES */}
        {PharmaRouter()}
        {AdminRouter()}
        {PharmacyAdminRouter()}

        {/* PUBLIC ROUTES */}
        {LoginRouter()}
      </Routes>
    </ThemeProvider>
  );
};

export default AppRouter;
